import { apiClient } from './api';

const request = (path, options = {}) => apiClient.requestPg(path, options);

const listResource = async (path, key) => {
    const data = await request(path);
    return Array.isArray(data?.[key]) ? data[key] : [];
};

const unwrapWithKey = (data, key) => {
    if (!key) {
        return data;
    }
    if (data && typeof data === 'object' && key in data) {
        return data[key];
    }
    return data;
};

const getResource = async (path, key) => {
    const data = await request(path);
    return unwrapWithKey(data, key);
};

const createResource = (path, payload, key) =>
    request(path, { method: 'POST', body: payload }).then((data) => unwrapWithKey(data, key));

const updateResource = (path, payload, key) =>
    request(path, { method: 'PUT', body: payload }).then((data) => unwrapWithKey(data, key));

const deleteResource = (path) => request(path, { method: 'DELETE' });

export const pgApi = {
    // Users
    listUsers: () => listResource('users', 'users'),
    getUser: (id) => getResource(`users/${id}`, 'user'),
    createUser: (payload) => createResource('users', payload, 'user'),
    updateUser: (id, payload) => updateResource(`users/${id}`, payload, 'user'),
    deleteUser: (id) => deleteResource(`users/${id}`),

    // Locations
    listLocations: () => listResource('locations', 'locations'),
    listUserLocations: (userId) => listResource(`users/${userId}/locations`, 'locations'),
    getLocation: (id) => getResource(`locations/${id}`, 'location'),
    createLocation: (payload) => createResource('locations', payload, 'location'),
    updateLocation: (id, payload) => updateResource(`locations/${id}`, payload, 'location'),
    deleteLocation: (id) => deleteResource(`locations/${id}`),
    createUserLocation: (userId, payload) => createResource(`users/${userId}/locations`, payload, 'location'),
    updateUserLocation: (userId, id, payload) => updateResource(`users/${userId}/locations/${id}`, payload, 'location'),
    deleteUserLocation: (userId, id) => deleteResource(`users/${userId}/locations/${id}`),

    // Location floors
    listFloors: () => listResource('location-floors', 'location_floors'),
    listUserFloors: (userId) => listResource(`users/${userId}/location-floors`, 'location_floors'),
    getFloor: (id) => getResource(`location-floors/${id}`, 'location_floor'),
    createFloor: (payload) => createResource('location-floors', payload, 'location_floor'),
    updateFloor: (id, payload) => updateResource(`location-floors/${id}`, payload, 'location_floor'),
    deleteFloor: (id) => deleteResource(`location-floors/${id}`),
    createUserFloor: (userId, payload) => createResource(`users/${userId}/location-floors`, payload, 'location_floor'),
    updateUserFloor: (userId, id, payload) => updateResource(`users/${userId}/location-floors/${id}`, payload, 'location_floor'),
    deleteUserFloor: (userId, id) => deleteResource(`users/${userId}/location-floors/${id}`),

    // Location rooms
    listRooms: () => listResource('location-rooms', 'location_rooms'),
    listUserRooms: (userId) => listResource(`users/${userId}/location-rooms`, 'location_rooms'),
    getRoom: (id) => getResource(`location-rooms/${id}`, 'location_room'),
    createRoom: (payload) => createResource('location-rooms', payload, 'location_room'),
    updateRoom: (id, payload) => updateResource(`location-rooms/${id}`, payload, 'location_room'),
    deleteRoom: (id) => deleteResource(`location-rooms/${id}`),
    createUserRoom: (userId, payload) => createResource(`users/${userId}/location-rooms`, payload, 'location_room'),
    updateUserRoom: (userId, id, payload) => updateResource(`users/${userId}/location-rooms/${id}`, payload, 'location_room'),
    deleteUserRoom: (userId, id) => deleteResource(`users/${userId}/location-rooms/${id}`),

    // Billing addresses
    listBillingAddresses: () => listResource('billing-addresses', 'billing_addresses'),
    listUserBillingAddresses: (userId) => listResource(`users/${userId}/billing-addresses`, 'billing_addresses'),
    createBillingAddress: (payload) => createResource('billing-addresses', payload, 'billing_address'),
    updateBillingAddress: (id, payload) => updateResource(`billing-addresses/${id}`, payload, 'billing_address'),
    deleteBillingAddress: (id) => deleteResource(`billing-addresses/${id}`),
    createUserBillingAddress: (userId, payload) => createResource(`users/${userId}/billing-addresses`, payload, 'billing_address'),
    updateUserBillingAddress: (userId, id, payload) => updateResource(`users/${userId}/billing-addresses/${id}`, payload, 'billing_address'),
    deleteUserBillingAddress: (userId, id) => deleteResource(`users/${userId}/billing-addresses/${id}`),

    // ISP details
    listIspDetails: () => listResource('isp-details', 'isp_details'),
    listUserIspDetails: (userId) => listResource(`users/${userId}/isp-details`, 'isp_details'),
    createIspDetail: (payload) => createResource('isp-details', payload, 'isp_detail'),
    updateIspDetail: (id, payload) => updateResource(`isp-details/${id}`, payload, 'isp_detail'),
    deleteIspDetail: (id) => deleteResource(`isp-details/${id}`),
    createUserIspDetail: (userId, payload) => createResource(`users/${userId}/isp-details`, payload, 'isp_detail'),
    updateUserIspDetail: (userId, id, payload) => updateResource(`users/${userId}/isp-details/${id}`, payload, 'isp_detail'),
    deleteUserIspDetail: (userId, id) => deleteResource(`users/${userId}/isp-details/${id}`),

    // Routers
    listRouters: () => listResource('routers', 'routers'),
    listUserRouters: (userId) => listResource(`users/${userId}/routers`, 'routers'),
    createRouter: (payload) => createResource('routers', payload, 'router'),
    updateRouter: (id, payload) => updateResource(`routers/${id}`, payload, 'router'),
    deleteRouter: (id) => deleteResource(`routers/${id}`),
    createUserRouter: (userId, payload) => createResource(`users/${userId}/routers`, payload, 'router'),
    updateUserRouter: (userId, id, payload) => updateResource(`users/${userId}/routers/${id}`, payload, 'router'),
    deleteUserRouter: (userId, id) => deleteResource(`users/${userId}/routers/${id}`),

    // Access points
    listAccessPoints: () => listResource('access-points', 'access_points'),
    listUserAccessPoints: (userId) => listResource(`users/${userId}/access-points`, 'access_points'),
    createAccessPoint: (payload) => createResource('access-points', payload, 'access_point'),
    updateAccessPoint: (id, payload) => updateResource(`access-points/${id}`, payload, 'access_point'),
    deleteAccessPoint: (id) => deleteResource(`access-points/${id}`),
    createUserAccessPoint: (userId, payload) => createResource(`users/${userId}/access-points`, payload, 'access_point'),
    updateUserAccessPoint: (userId, id, payload) => updateResource(`users/${userId}/access-points/${id}`, payload, 'access_point'),
    deleteUserAccessPoint: (userId, id) => deleteResource(`users/${userId}/access-points/${id}`),

    // Radios
    listRadios: () => listResource('radios', 'radios'),
    listUserRadios: (userId) => listResource(`users/${userId}/radios`, 'radios'),
    createRadio: (payload) => createResource('radios', payload, 'radio'),
    updateRadio: (id, payload) => updateResource(`radios/${id}`, payload, 'radio'),
    deleteRadio: (id) => deleteResource(`radios/${id}`),
    createUserRadio: (userId, payload) => createResource(`users/${userId}/radios`, payload, 'radio'),
    updateUserRadio: (userId, id, payload) => updateResource(`users/${userId}/radios/${id}`, payload, 'radio'),
    deleteUserRadio: (userId, id) => deleteResource(`users/${userId}/radios/${id}`),

    // Devices
    listDevices: () => listResource('devices', 'devices'),
    listUserDevices: (userId) => listResource(`users/${userId}/devices`, 'devices'),
    createDevice: (payload) => createResource('devices', payload, 'device'),
    updateDevice: (id, payload) => updateResource(`devices/${id}`, payload, 'device'),
    deleteDevice: (id) => deleteResource(`devices/${id}`),
    createUserDevice: (userId, payload) => createResource(`users/${userId}/devices`, payload, 'device'),
    updateUserDevice: (userId, id, payload) => updateResource(`users/${userId}/devices/${id}`, payload, 'device'),
    deleteUserDevice: (userId, id) => deleteResource(`users/${userId}/devices/${id}`),

    // Provisions
    listProvisions: () => listResource('provisions', 'provisions'),
    listUserProvisions: (userId) => listResource(`users/${userId}/provisions`, 'provisions'),
    createProvision: (payload) => createResource('provisions', payload, 'provision'),
    updateProvision: (id, payload) => updateResource(`provisions/${id}`, payload, 'provision'),
    deleteProvision: (id) => deleteResource(`provisions/${id}`),
    createUserProvision: (userId, payload) => createResource(`users/${userId}/provisions`, payload, 'provision'),
    updateUserProvision: (userId, id, payload) => updateResource(`users/${userId}/provisions/${id}`, payload, 'provision'),
    deleteUserProvision: (userId, id) => deleteResource(`users/${userId}/provisions/${id}`),

    // Deployments
    listDeployments: () => listResource('deployments', 'deployments'),
    listUserDeployments: (userId) => listResource(`users/${userId}/deployments`, 'deployments'),
    createDeployment: (payload) => createResource('deployments', payload, 'deployment'),
    updateDeployment: (id, payload) => updateResource(`deployments/${id}`, payload, 'deployment'),
    deleteDeployment: (id) => deleteResource(`deployments/${id}`),
    createUserDeployment: (userId, payload) => createResource(`users/${userId}/deployments`, payload, 'deployment'),
    updateUserDeployment: (userId, id, payload) => updateResource(`users/${userId}/deployments/${id}`, payload, 'deployment'),
    deleteUserDeployment: (userId, id) => deleteResource(`users/${userId}/deployments/${id}`),
};

export const pgRequests = { request, listResource };
